// Procedural surface textures for the 3D preview, drawn to an offscreen canvas so
// no external assets are needed (the app runs under a strict CSP). Textures are
// cached per colour and tile in world millimetres via RepeatWrapping, so a single
// small canvas covers walls of any size at a consistent physical scale.

import * as THREE from 'three';

const cache = new Map<string, THREE.Texture | null>();

function canvas(size: number): { c: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  if (typeof document === 'undefined') return null; // node (GLB export) — skip textures
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  return ctx ? { c, ctx } : null;
}

function finalize(c: HTMLCanvasElement, mmX: number, mmY: number): THREE.Texture {
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(1 / mmX, 1 / mmY); // the canvas spans mmX × mmY millimetres of surface
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

/** `base` scaled by `k` (k>1 brightens), as an rgb() string. */
function tint(base: THREE.Color, k: number): string {
  const ch = (v: number): number => Math.max(0, Math.min(255, Math.round(v * 255 * k)));
  return `rgb(${ch(base.r)}, ${ch(base.g)}, ${ch(base.b)})`;
}

/** Fine vertical tongue-and-groove slats with light grain — for sauna walls.
 *  Modelled on a clear maple/aspen panel: ~40 narrow slats span 2510 mm across, the
 *  slats running vertically on the wall (the canvas x axis maps to the wall's
 *  horizontal direction). `seams = false` is the grain alone, one tone — for a
 *  bench, whose slats are real geometry: painted seams would score fake grooves
 *  across them. */
export function woodTexture(baseHex: string, seams = true): THREE.Texture | null {
  const key = `wood:${baseHex}:${seams}`;
  if (cache.has(key)) return cache.get(key)!;
  const S = 1024;
  const m = canvas(S);
  if (!m) { cache.set(key, null); return null; }
  const { c, ctx } = m;
  const base = new THREE.Color(baseHex);
  const slats = 40, sw = S / slats; // ~62.75 mm per slat over 2510 mm
  for (let i = 0; i < slats; i++) {
    const x0 = i * sw;
    // Slight per-slat tone so adjacent slats read apart, like natural timber.
    ctx.fillStyle = tint(base, seams ? 0.96 + ((i * 53) % 9) / 100 : 1);
    ctx.fillRect(x0, 0, sw + 1, S);
    // Fine vertical grain: a few faint darker streaks meandering down each slat.
    ctx.globalAlpha = 0.05;
    ctx.strokeStyle = tint(base, 0.7);
    ctx.lineWidth = 1;
    const streaks = 5;
    for (let g = 1; g <= streaks; g++) {
      const gx = x0 + (sw * g) / (streaks + 1) + ((i * 17 + g * 29) % 5) - 2;
      ctx.beginPath();
      for (let y = 0; y <= S; y += 8) {
        ctx.lineTo(gx + Math.sin(y * 0.03 + i * 1.3 + g) * 1.4, y);
      }
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    if (!seams) continue;
    // Tongue-and-groove seam: a thin shadow with a light highlight beside it.
    ctx.strokeStyle = tint(base, 0.62);
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(x0, 0); ctx.lineTo(x0, S); ctx.stroke();
    ctx.strokeStyle = tint(base, 1.06);
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(x0 + 1.5, 0); ctx.lineTo(x0 + 1.5, S); ctx.stroke();
  }
  const t = finalize(c, 2510, 2510); // 251 cm across the 40 slats; grain tiles every 2.51 m
  cache.set(key, t);
  return t;
}

/** A glossy ceramic tile grid with grout lines — for steam-room walls/benches. */
export function tileTexture(baseHex: string): THREE.Texture | null {
  const key = `tile:${baseHex}`;
  if (cache.has(key)) return cache.get(key)!;
  const m = canvas(256);
  if (!m) { cache.set(key, null); return null; }
  const { c, ctx } = m;
  const base = new THREE.Color(baseHex);
  ctx.fillStyle = tint(base, 1); ctx.fillRect(0, 0, 256, 256);
  const tiles = 2, ts = 256 / tiles;
  // Subtle tile-to-tile shading and a soft top-left highlight per tile.
  for (let iy = 0; iy < tiles; iy++) for (let ix = 0; ix < tiles; ix++) {
    ctx.fillStyle = tint(base, 0.97 + ((ix + iy) % 2) * 0.05);
    ctx.fillRect(ix * ts, iy * ts, ts, ts);
    const g = ctx.createLinearGradient(ix * ts, iy * ts, ix * ts + ts, iy * ts + ts);
    g.addColorStop(0, 'rgba(255,255,255,0.14)'); g.addColorStop(0.5, 'rgba(255,255,255,0)');
    ctx.fillStyle = g; ctx.fillRect(ix * ts, iy * ts, ts, ts);
  }
  // Grout lines.
  ctx.strokeStyle = tint(base, 0.7);
  ctx.lineWidth = 4;
  for (let i = 0; i <= tiles; i++) {
    const p = i * ts;
    ctx.beginPath(); ctx.moveTo(p, 0); ctx.lineTo(p, 256); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, p); ctx.lineTo(256, p); ctx.stroke();
  }
  const t = finalize(c, 400, 400); // 200 mm tiles
  cache.set(key, t);
  return t;
}
